import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "sonner";
import * as m from "@/paraglide/messages";
import {
  listWidgets,
  createWidget,
  deleteWidget,
  type WidgetCreate,
  type WidgetType,
} from "@/lib/widgets-api";
import {createFavorite, deleteFavorite} from "@/lib/favorites-api";
import type {PostWidget, ChartWidget} from "@/lib/chat-runtime";

const WIDGETS_KEY = "widgets";
const FAVORITES_KEY = "favorites";

/**
 * Hook to fetch user's widgets
 */
export function useWidgets(widgetType?: WidgetType) {
  return useQuery({
    queryKey: [WIDGETS_KEY, {widgetType}],
    queryFn: () => listWidgets(widgetType),
  });
}

/**
 * Hook to create a widget
 */
export function useCreateWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: WidgetCreate) => createWidget(request),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [WIDGETS_KEY]});
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to delete a widget
 */
export function useDeleteWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (widgetId: string) => deleteWidget(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [WIDGETS_KEY]});
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Hook to create a widget and add it to favorites in one operation.
 *
 * This is the primary hook for saving widgets from chat - it:
 * 1. Creates the widget in the database
 * 2. Adds it to the user's favorites
 */
export function useCreateWidgetAndFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: WidgetCreate) => {
      // Step 1: Create the widget
      const widget = await createWidget(request);

      // Step 2: Add the widget to favorites
      await createFavorite({
        entity_type: "widget",
        entity_id: widget.id,
      });

      return widget;
    },
    onSuccess: () => {
      // Invalidate both widgets and favorites queries
      queryClient.invalidateQueries({queryKey: [WIDGETS_KEY]});
      queryClient.invalidateQueries({queryKey: [FAVORITES_KEY]});
      toast.success("Added to favorites");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

interface ToggleFavoriteParams {
  widget: PostWidget | ChartWidget;
  widgetType: WidgetType;
  existingFavoriteId?: string;
  existingSavedWidgetId?: string;
}

interface ToggleFavoriteResult {
  action: "added" | "removed";
  favoriteId?: string;
  savedWidgetId?: string;
}

/**
 * Hook to toggle a widget's favorite status.
 *
 * - If not favorited: creates the widget and adds it to favorites
 * - If already favorited: removes from favorites and deletes the widget
 */
export function useToggleFavoriteWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      widget,
      widgetType,
      existingFavoriteId,
      existingSavedWidgetId,
    }: ToggleFavoriteParams): Promise<ToggleFavoriteResult> => {
      if (existingFavoriteId) {
        // Unfavorite: delete favorite first, then the widget
        await deleteFavorite(existingFavoriteId);
        if (existingSavedWidgetId) {
          await deleteWidget(existingSavedWidgetId);
        }
        return {action: "removed"};
      } else {
        // Favorite: create widget and add to favorites
        const savedWidget = await createWidget({widget_type: widgetType, data: widget});
        const favorite = await createFavorite({entity_type: "widget", entity_id: savedWidget.id});
        return {action: "added", favoriteId: favorite.id, savedWidgetId: savedWidget.id};
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({queryKey: [WIDGETS_KEY]});
      queryClient.invalidateQueries({queryKey: [FAVORITES_KEY]});
      if (result.action === "added") {
        toast.success(m.favorites_added());
      } else {
        toast.success(m.favorites_removed());
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
